import React from "react";
import { InputComponent } from "@/store/sagas/types/deviceUnlock";
import { InfoIcon, VerifiedIcon } from "./CommonComponents/IconsComponent";
import ModalComponent from "./CommonComponents/ModalComponent";
import { generateListKey } from "@/lib/utils/keyGenerator";
import { sanitizeHtml } from "@/lib/utils/safeHtml";

interface InputRendererProps {
    input: InputComponent;
    onValidationChange?: (inputId: string, hasError: boolean) => void;
}

const IMEI_REGEX = /^\d{14,16}$/;

const InputRenderer: React.FC<InputRendererProps> = ({ input, onValidationChange }) => {
  const isImeiField = input.id === 'imei_field';

  const labelText = isImeiField ? 'IMEI' : input.title;
  const defaultValue = '';
  // Design tokens
  const darkBlueText = '#1F1F3F';
  const lightBorder = '#E3E3E3';

    const [inputValue, setInputValue] = React.useState<string>(defaultValue);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [isTouched, setIsTouched] = React.useState(false);
  const [showInfoModal, setShowInfoModal] = React.useState(false);

    const validateValue = React.useCallback((value: string) => {
        if (input.required && !value.trim()) {
            return input.errorMessages.required;
        }

        const trimmed = value.trim();
        if (!IMEI_REGEX.test(trimmed)) {
            return input.errorMessages.invalid;
        }

        return '';
    }, [input.required, input.errorMessages]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = e.target.value;
        setInputValue(nextValue);
        setErrorMessage(validateValue(nextValue));
    };

    const handleInputFocus = () => {
        setIsTouched(true);
        setErrorMessage(validateValue(inputValue));
    };

    const handleInputBlur = () => {
        setIsTouched(true);
        setErrorMessage(validateValue(inputValue));
    };

        React.useEffect(() => {
            const hasError = Boolean(validateValue(inputValue));
            onValidationChange?.(input.id, hasError);
        }, [input.id, inputValue, onValidationChange, validateValue]);

    const isImeiValid = isImeiField && IMEI_REGEX.test(inputValue?.trim());

    // Shared modal content
    const modalContent = input.modalContent && input.modalContent.length > 0 && (
        <div className="text-gray-800 text-sm space-y-4">
            {input.modalContent.map((content, index) => (
                <div 
                    key={generateListKey('cta-modal', content, index)}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} 
                />
            ))}
            <div className="mt-4 flex justify-center">
              <button
                className="bg-black text-white w-full max-w-[430px] h-12 rounded-full font-semibold text-sm hover:bg-gray-800 transition-colors"
                onClick={() => setShowInfoModal(false)}
              >
                {input.modalButtonText || 'Close'}
              </button>
            </div>
        </div>
    )

  return (
    <div className="mx-auto mb-6" style={{ width: '343px' }}>
                <div className="flex items-center mb-2">
                    <label
                        htmlFor={input.id}
                        className={`block text-sm font-normal text-[${darkBlueText}]`}
                        style={{ fontFamily: 'Verizon NHG DS, Helvetica Neue, Helvetica, Arial, sans-serif' }}
                    >
                        {labelText}
                        {input.required && <span className="ml-1">*</span>}
                    </label>
                    {/* Add Info Icon specifically for IMEI field */}
                    {isImeiField && (
                        <button
                            type="button"
                            aria-label={input.modalTitle?.replaceAll(/<[^>]+>/g, '')}
                            onClick={() => {
                                if (input.modalTitle && input.modalContent) {
                                    setShowInfoModal(true);
                                }
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                            className="border-0 bg-transparent p-0 cursor-pointer inline-flex items-center"
                        >
                            <InfoIcon />
                        </button>
                    )}
                </div>
    
                <div className="relative">
                    <input
                        id={input.id}
                        name={input.id}
                        type={input.type}
                        pattern={isImeiField ? '[0-9]{14,16}' : undefined}
                        required={input.required}
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        aria-invalid={!!errorMessage}
                        aria-describedby={errorMessage ? `${input.id}-error` : undefined}
                        className={`w-full px-3 py-2 border border-[${lightBorder}] rounded-xl text-[${darkBlueText}] bg-white focus:outline-none focus:ring-1 focus:ring-[${darkBlueText}] focus:border-[${darkBlueText}] transition-all ${isImeiField ? 'pr-10' : ''}`}
                        style={{ fontFamily: 'Verizon NHG DS, Helvetica Neue, Helvetica, Arial, sans-serif' }}
                    />
                    {/* Add Verified Icon for IMEI field only if it has 15 digits */}
                    {isImeiValid && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <VerifiedIcon />
                        </div>
                    )}
                </div>

                {isTouched && errorMessage && (
                    <p
                        id={`${input.id}-error`}
                        className="mt-1 text-sm text-left text-red-600"
                        style={{ fontFamily: 'Verizon NHG DS, Helvetica Neue, Helvetica, Arial, sans-serif' }}
                    >
                        {errorMessage}
                    </p>
                )}

                {/* IMEI Info Modal */}
                {isImeiField && input.modalTitle && input.modalContent && (
                    <ModalComponent
                        open={showInfoModal}
                        onClose={() => setShowInfoModal(false)}
                        title={<span dangerouslySetInnerHTML={{ __html: sanitizeHtml(input.modalTitle) }} />}
                        maxWidth="xs"
                    >
                        {modalContent}
                    </ModalComponent>
                )}
    </div>
  );
};

export default InputRenderer;